import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { getBlogPost } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { AdSlot } from '@/components/ads/AdSlot';
import { BLOG } from '@/constants/testIds';

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        getBlogPost(slug)
            .then(setPost)
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <Seo title="Article not found" canonical={`/blog/${slug}`} />
                <h1 className="text-3xl font-semibold text-slate-900">Article not found</h1>
                <p className="mt-2 text-slate-600">
                    We couldn’t find that post. It may have been renamed.
                </p>
                <Link
                    to="/blog"
                    className="mt-4 inline-block text-[hsl(var(--primary))] hover:underline"
                >
                    Back to the blog
                </Link>
            </section>
        );
    }

    if (!post) {
        return (
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="mt-4 h-9 w-4/5" />
                <Skeleton className="mt-6 aspect-[16/9] w-full rounded-xl" />
                <div className="mt-6 space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-10/12" />
                </div>
            </section>
        );
    }

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        image: post.hero_image ? [post.hero_image] : undefined,
        datePublished: post.published_at,
        author: { '@type': 'Organization', name: post.author },
        publisher: { '@type': 'Organization', name: 'UtilityTool' },
    };

    return (
        <>
            <Seo
                title={post.title}
                description={post.excerpt}
                canonical={`/blog/${post.slug}`}
                ogImage={post.hero_image}
                type="article"
                structuredData={structured}
            />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid={BLOG.article}>
                <Breadcrumb items={[{ label: 'Blog', to: '/blog' }, { label: post.title }]} />
                <div className="mt-4 flex items-center gap-2">
                    <Badge variant="secondary" className="bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        {post.category}
                    </Badge>
                    <span className="text-xs text-slate-500">
                        {new Date(post.published_at).toLocaleDateString(undefined, {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                        &nbsp;·&nbsp;{post.reading_minutes} min read
                    </span>
                </div>
                <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    {post.title}
                </h1>
                <p className="mt-3 text-slate-600">By {post.author}</p>
                {post.hero_image ? (
                    <div className="mt-6 rounded-xl overflow-hidden bg-slate-100 aspect-[16/9]">
                        <img
                            src={post.hero_image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            width={1200}
                            height={675}
                        />
                    </div>
                ) : null}
                <div
                    className="prose mt-8 mx-auto"
                    dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
                />
                <AdSlot placement="blog-inline" />
                <div className="mt-12 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-5">
                    <p className="text-sm font-semibold text-slate-900">Try a related tool</p>
                    <p className="mt-1 text-sm text-slate-600">
                        Small tools that pair nicely with this article.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Link to="/tools/word-to-pdf" className="text-sm text-[hsl(var(--primary))] hover:underline">Word to PDF</Link>
                        <Link to="/tools/image-compressor" className="text-sm text-[hsl(var(--primary))] hover:underline">Image Compressor</Link>
                        <Link to="/tools/jpg-to-png" className="text-sm text-[hsl(var(--primary))] hover:underline">JPG to PNG</Link>
                    </div>
                </div>
            </article>
        </>
    );
}
